import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CoachButton, Field, PasswordInput, inputClass } from '../components/primitives';
import {
  apiErrorMessage,
  getSignupTerms,
  login,
  MEMBER_URL,
  signup,
  type PublicConfig,
} from '../lib/api';
import { storedRef } from '../lib/ref';
import { AuthFrame } from './auth-frame';

/**
 * SC-04. Creates a platform member account (one account for the coach app and
 * the store).
 *
 * `?join=<productId>` is a visitor joining to buy a course: under invite-only
 * signup it counts as the invitation where the tenant allows it
 * (`campaignPageSignupAllowed`, verified again by the server), and the new
 * member goes straight back to that course instead of the coaching onboarding.
 */
export function SignupPage({ config }: { config: PublicConfig }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ref = params.get('ref') ?? storedRef();
  const joinId = params.get('join');
  const joiningCourse = !!joinId && config.campaignPageSignupAllowed;
  const coursePath = joinId ? `/learn/c/${encodeURIComponent(joinId)}` : null;
  const loginHref = coursePath ? `/login?next=${encodeURIComponent(coursePath)}` : '/login';
  const terms = useQuery({
    queryKey: ['signup-terms'],
    queryFn: getSignupTerms,
    staleTime: 5 * 60_000,
  });

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [sponsorCode, setSponsorCode] = useState(ref);
  const [agreed, setAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const linksOnlyBlocked = config.signupMode === 'links_only' && !ref && !joiningCourse;
  const sponsorRequired = config.signupMode === 'by_sponsor_only' && !joiningCourse;
  const fullRegistrationHref = `${MEMBER_URL}/signup${ref ? `?ref=${encodeURIComponent(ref)}` : ''}`;

  if (linksOnlyBlocked) {
    return (
      <AuthFrame
        eyebrow="By invitation"
        title="Invite only"
        subtitle="Miracles Protocol accounts are created from an invitation link. Open the link you were sent to continue."
      >
        <Link to={loginHref} className="text-sm text-ink underline underline-offset-4">
          I already have an account
        </Link>
      </AuthFrame>
    );
  }
  if (config.needsFullRegistration) {
    return (
      <AuthFrame
        eyebrow="Create your account"
        title="Register on the store first"
        subtitle="Your account needs a few more registration details. Complete them on the store, then log in here with the same email and password."
      >
        <a
          href={fullRegistrationHref}
          className="flex min-h-11 w-full items-center justify-center bg-gold font-mono text-[0.68rem] uppercase tracking-[0.14em] text-ink"
        >
          Continue to registration
        </a>
      </AuthFrame>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError('Use at least 8 characters for your password.');
    if (password !== confirm) return setError('The passwords do not match.');
    if (sponsorRequired && !sponsorCode.trim())
      return setError('A sponsor code is required to sign up.');
    if (terms.data?.requireOnSignup && !agreed)
      return setError('Please accept the Terms and Privacy Policy to continue.');
    setBusy(true);
    try {
      await signup({
        email: email.trim(),
        password,
        confirmPassword: confirm,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        username: terms.data?.requireUsernameOnSignup ? username.trim() : undefined,
        sponsorCode: sponsorCode.trim() || undefined,
        termsAccepted: terms.data?.requireOnSignup ? agreed : undefined,
        termsVersion: terms.data?.requireOnSignup ? terms.data.termsVersion : undefined,
        birthDate: config.birthDateField !== 'hidden' && birthDate ? birthDate : undefined,
        joinCourseProductId: joinId ?? undefined,
      });
      await login({ identifier: email.trim(), password });
      navigate(coursePath ?? '/onboarding', { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create your account. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame
      eyebrow="Create your account"
      title={coursePath ? 'Join Miracles Protocol' : 'Start your Success Plan'}
      subtitle={coursePath ? 'Then you go straight back to the course.' : undefined}
      footer={
        <p>
          Already have an account?{' '}
          <Link to={loginHref} className="text-ink underline underline-offset-4">
            Log in
          </Link>
        </p>
      }
    >
      <form onSubmit={onSubmit} className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" htmlFor="first">
            <input
              id="first"
              className={inputClass}
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </Field>
          <Field label="Last name" htmlFor="last">
            <input
              id="last"
              className={inputClass}
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </Field>
        </div>
        <Field label="Email address" htmlFor="email">
          <input
            id="email"
            type="email"
            className={inputClass}
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Field>
        {config.birthDateField !== 'hidden' && (
          <Field
            label={
              config.birthDateField === 'required' ? 'Date of birth' : 'Date of birth (optional)'
            }
            htmlFor="birth-date"
          >
            <input
              id="birth-date"
              type="date"
              className={inputClass}
              max={new Date().toISOString().slice(0, 10)}
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              required={config.birthDateField === 'required'}
            />
          </Field>
        )}
        {terms.data?.requireUsernameOnSignup && (
          <Field label="Username" htmlFor="username">
            <input
              id="username"
              className={inputClass}
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </Field>
        )}
        <Field label="Create password" htmlFor="password" hint="At least 8 characters.">
          <PasswordInput
            id="password"
            autoComplete="new-password"
            value={password}
            onChange={setPassword}
            required
          />
        </Field>
        <Field label="Confirm password" htmlFor="confirm">
          <PasswordInput
            id="confirm"
            autoComplete="new-password"
            value={confirm}
            onChange={setConfirm}
            required
          />
        </Field>
        {(sponsorRequired || ref) && (
          <Field
            label="Sponsor code"
            htmlFor="sponsor"
            hint={ref ? 'From the link your sponsor shared.' : undefined}
          >
            <input
              id="sponsor"
              className={inputClass}
              value={sponsorCode}
              onChange={(e) => setSponsorCode(e.target.value)}
              readOnly={Boolean(ref)}
              required={sponsorRequired}
            />
          </Field>
        )}
        {terms.data?.requireOnSignup && (
          <div className="space-y-2">
            <label className="flex items-start gap-2.5 text-sm text-ink/80">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-[#5F6A62]"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              {/* In-app routes, not the member subdomain: the coach app serves
                  these itself, so they work offline in the packaged app and
                  need no host that may not exist on a given box. */}
              <span>
                I accept the{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-4 hover:text-ink"
                >
                  Terms
                </a>{' '}
                and{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-4 hover:text-ink"
                >
                  Privacy Policy
                </a>
                .
              </span>
            </label>
            <button
              type="button"
              className="eyebrow underline underline-offset-4 hover:text-ink"
              onClick={() => setShowTerms((v) => !v)}
            >
              {showTerms ? 'Hide terms' : 'Read terms'}
            </button>
            {showTerms && (
              <div className="max-h-64 overflow-y-auto border border-border p-3 text-xs leading-relaxed text-ink/70 whitespace-pre-wrap">
                {terms.data.termsText}
                {terms.data.privacyText && `\n\n${terms.data.privacyText}`}
              </div>
            )}
          </div>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <CoachButton kind="gold" type="submit" block disabled={busy || terms.isPending}>
          {busy ? 'Creating your account' : 'Create account'}
        </CoachButton>
      </form>
    </AuthFrame>
  );
}
